// src/lib/devActor.ts
// Кто на самом деле работает, когда разработчик вошёл через dev-мок.
//
// Мок (`VITE_MOCK_EMAIL` / `VITE_MOCK_TELEGRAM_ID`) — это настоящий вход под
// чужой учёткой, поэтому сервер видит владельца учётки и никак не может узнать
// исполнителя: подпись честная, отличить её не от чего. Единственный источник
// правды здесь — сам разработчик, поэтому он один раз кладёт свою почту в
// `.env.local` рядом с почтой мока:
//
//   VITE_MOCK_EMAIL=levita.teacher97@gmail.com   # под кем смотрим
//   VITE_DEV_ACTOR_EMAIL=da.gufranov@gmail.com   # кто смотрит
//
// Почта меняется на user_id один раз за сессию через `resolve_dev_actor`, и та
// отдаёт только активных участников команды разработки — то есть назначить часы
// произвольному человеку нельзя. Читатели журнала (`get_team_activity_stats`,
// `get_user_screen_log`) проверяют членство ещё раз, уже на своей стороне.
//
// ⚠️ Всё ниже живёт только в dev-сборке: `import.meta.env.DEV` в прод-сборке
// равен false, константа сворачивается в пустую строку, и минификатор выкидывает
// ветки целиком — прод-клиент продолжает слать ровно прежний набор полей.

import { getSupabase } from '@/lib/supabase'

const DEV_ACTOR_EMAIL = import.meta.env.DEV
  ? String(import.meta.env.VITE_DEV_ACTOR_EMAIL ?? '').trim().toLowerCase()
  : ''

/** Событие пришло из локальной сборки, а не из боевого клиента. */
export const IS_DEV_SESSION = Boolean(import.meta.env.DEV)

const STORAGE_KEY = `dev_actor_user_id:${DEV_ACTOR_EMAIL}`

let resolvedUserId: string | null = null
let resolving: Promise<string | null> | null = null

function readCache(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeCache(userId: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, userId)
  } catch {
    // приватный режим — обойдёмся памятью процесса
  }
}

/**
 * Кто работает, если это не владелец учётки. Синхронно и без запросов —
 * значение подставляет `ensureDevActorResolved`, поэтому трекер экранов не
 * ждёт сети и не теряет события, пока идёт резолв.
 */
export function getDevActorUserId(): string | null {
  if (!DEV_ACTOR_EMAIL) return null
  if (resolvedUserId) return resolvedUserId
  resolvedUserId = readCache()
  return resolvedUserId
}

/** Резолвит почту разработчика в user_id. Зовётся после входа, один раз. */
export async function ensureDevActorResolved(): Promise<string | null> {
  if (!DEV_ACTOR_EMAIL) return null
  if (resolvedUserId) return resolvedUserId

  const cached = readCache()
  if (cached) {
    resolvedUserId = cached
    return cached
  }
  if (resolving) return resolving

  resolving = (async () => {
    try {
      const { data, error } = await (getSupabase() as any).rpc('resolve_dev_actor', {
        p_email: DEV_ACTOR_EMAIL,
      })
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : null
      if (!row?.user_id) {
        console.warn(
          `DEV: VITE_DEV_ACTOR_EMAIL=${DEV_ACTOR_EMAIL} не найден среди участников команды разработки — ` +
            'часы останутся на учётке, под которой выполнен вход'
        )
        return null
      }
      resolvedUserId = row.user_id
      writeCache(row.user_id)
      console.log(`DEV: события экранов пишутся на ${row.display_name ?? DEV_ACTOR_EMAIL}`)
      return resolvedUserId
    } catch (e) {
      // Разметка активности — не критичный путь: не нашлось, не хватило прав,
      // не применена миграция — работаем дальше без неё.
      console.warn('DEV: не удалось определить разработчика для журнала экранов', e)
      return null
    } finally {
      resolving = null
    }
  })()

  return resolving
}
